import { type FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import type { ConsentStatus } from '../../lib/types';
import { useSessionChanged } from '../../lib/session';
import { ErrorMessage, Loading, PageTitle } from '../../components/ui';

/** Shown until the current versions of the terms and the GDPR notice are accepted. */
export function ConsentPage() {
  const navigate = useNavigate();
  const sessionChanged = useSessionChanged();
  const consents = useQuery({ queryKey: ['consents'], queryFn: () => api.get<ConsentStatus[]>('/api/v1/account/consents') });
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!consents.data) return <Loading />;
  const termsDoc = consents.data.find((c) => c.document === 'Terms');
  const privacyDoc = consents.data.find((c) => c.document === 'Privacy');
  if (!termsDoc || !privacyDoc) return <Loading />;
  const versions = { termsVersion: termsDoc.version, privacyVersion: privacyDoc.version };

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await api.post('/api/v1/account/consents', versions);
      await sessionChanged();
      navigate('/', { replace: true });
    } catch (e) {
      setError(e);
    }
  }

  return (
    <section className="auth-card">
      <PageTitle>Zanim zaczniesz</PageTitle>
      <p>Aby korzystać z systemu, zapoznaj się z regulaminem i informacją o przetwarzaniu danych osobowych.</p>
      <ErrorMessage error={error} />
      <form onSubmit={(e) => void submit(e)}>
        <label className="checkbox">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} required />
          <span>
            Akceptuję{' '}
            <a href={termsDoc.url} target="_blank" rel="noopener noreferrer">
              regulamin
            </a>{' '}
            (wersja {termsDoc.version}).
          </span>
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} required />
          <span>
            Zapoznałem/-am się z{' '}
            <a href={privacyDoc.url} target="_blank" rel="noopener noreferrer">
              klauzulą informacyjną RODO
            </a>{' '}
            (wersja {privacyDoc.version}).
          </span>
        </label>
        <button type="submit" disabled={!terms || !privacy}>
          Akceptuję i przechodzę dalej
        </button>
      </form>
    </section>
  );
}
